import React, {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  useDispatch,
} from "react-redux";

import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Leaf,
  LockKeyhole,
  ShoppingBag,
  Utensils,
} from "lucide-react";

import {
  fetchPriceSummary,
} from "../../actions/priceActions";

import {
  useCart,
} from "../../components/CartContext";

import "./css/CheckoutScreen.css";


/* =========================================================
   IMAGE URL HELPER
========================================================= */

const getImageUrl = (image) => {

  if (!image) {
    return "";
  }

  const imageValue =
    typeof image === "string"
      ? image
      : image.url ||
        image.image ||
        "";

  if (!imageValue) {
    return "";
  }

  if (
    imageValue.startsWith("http://") ||
    imageValue.startsWith("https://")
  ) {
    return imageValue;
  }

  return `${
    imageValue.startsWith("/")
      ? ""
      : "/"
  }${imageValue}`;

};


/* =========================================================
   CHECKOUT SCREEN
========================================================= */

function CheckoutScreen() {

  const navigate =
    useNavigate();

  const dispatch =
    useDispatch();

  const {
    restaurantId,
    tableId,
  } = useParams();


  /* =====================================================
     CART
  ===================================================== */

  const {
    cart,
    cartCount,
    cartTotal,
  } = useCart();


  /* =====================================================
     PRICE SUMMARY
  ===================================================== */

  const [
    priceSummary,
    setPriceSummary,
  ] = useState({

    subtotal: 0,

    tax: 0,

    serviceCharge: 0,

    total: 0,

  });


  /* =====================================================
     STATE
  ===================================================== */

  const [
    loading,
    setLoading,
  ] = useState(false);


  const [
    error,
    setError,
  ] = useState("");


  /* =====================================================
     HOME PATH
  ===================================================== */

  const homePath =
    `/${restaurantId}/table/${tableId}`;


  /* =====================================================
     FORMAT PRICE
  ===================================================== */

  const formatPrice = (value) => {

    return Number(
      value || 0
    ).toFixed(2);

  };


  /* =====================================================
     FETCH PRICE SUMMARY

     IMPORTANT:
     Backend calculates the actual
     subtotal, tax, service charge
     and total.

     Frontend sends only:
       menu_item_id
       quantity
  ===================================================== */

  useEffect(() => {

    const calculatePrice =
      async () => {

        if (
          !restaurantId ||
          !cart ||
          cart.length === 0
        ) {
          return;
        }


        try {

          setLoading(true);

          setError("");


          /* ---------------------------------------------
             PREPARE ITEMS
          --------------------------------------------- */

          const items =
            cart.map(
              (item) => ({

                menu_item_id:
                  item.id,

                quantity:
                  item.quantity,

              })
            );


          /* ---------------------------------------------
             BACKEND PRICE CALCULATION
          --------------------------------------------- */

          const response =
            await dispatch(

              fetchPriceSummary(
                restaurantId,
                items
              )

            );


          /* ---------------------------------------------
             SAVE BACKEND TOTAL
          --------------------------------------------- */

          setPriceSummary({

            subtotal:
              Number(
                response?.subtotal || 0
              ),

            tax:
              Number(
                response?.tax || 0
              ),

            serviceCharge:
              Number(
                response?.service_charge || 0
              ),

            total:
              Number(
                response?.total || 0
              ),

          });


        } catch (err) {

          console.error(
            "Price summary error:",
            err
          );


          setError(

            err?.response?.data?.detail ||

            err?.message ||

            "Unable to calculate your order total."

          );


        } finally {

          setLoading(false);

        }

      };


    calculatePrice();


  }, [
    dispatch,
    restaurantId,
    cart,
  ]);


  /* =====================================================
     BACK TO CART
  ===================================================== */

  const handleBackToCart = () => {

    navigate(
      `${homePath}/cart`
    );

  };


  /* =====================================================
     CONTINUE TO ORDER
  ===================================================== */

  const handleContinue = () => {

    /* ---------------------------------------------
       EMPTY CART
    --------------------------------------------- */

    if (
      !cart ||
      cart.length === 0
    ) {

      navigate(homePath);

      return;

    }


    /* ---------------------------------------------
       DON'T CONTINUE WHILE PRICE IS LOADING
    --------------------------------------------- */

    if (
      loading ||
      error
    ) {
      return;
    }


    /* ---------------------------------------------
       GO TO ORDER SCREEN

       Pass backend-calculated
       price summary.
    --------------------------------------------- */

    navigate(

      `${homePath}/order`,

      {

        state: {

          subtotal:
            Number(
              priceSummary.subtotal
            ),

          tax:
            Number(
              priceSummary.tax
            ),

          serviceCharge:
            Number(
              priceSummary.serviceCharge
            ),

          total:
            Number(
              priceSummary.total
            ),

        },

      }

    );

  };


  /* =====================================================
     EMPTY CART
  ===================================================== */

  if (
    !cart ||
    cart.length === 0
  ) {

    return (

      <main className="theme9-checkout-page">

        <section className="theme9-checkout-empty">


          {/* ICON */}

          <div className="theme9-checkout-empty-icon">

            <ShoppingBag
              size={34}
            />

          </div>


          {/* LABEL */}

          <span className="theme9-checkout-label">

            CHECKOUT

          </span>


          {/* TITLE */}

          <h1>
            Your cart is empty
          </h1>


          {/* DESCRIPTION */}

          <p>
            Add something fresh and
            delicious from our menu
            before continuing to
            checkout.
          </p>


          {/* BUTTON */}

          <button
            type="button"
            className="theme9-checkout-primary-button"
            onClick={() =>
              navigate(
                `${homePath}#theme9-menu`
              )
            }
          >

            Explore Our Menu

            <ArrowRight
              size={17}
            />

          </button>


          {/* DECORATION */}

          <div className="theme9-checkout-empty-leaf">

            <Leaf
              size={25}
            />

          </div>


        </section>

      </main>

    );

  }


  /* =====================================================
     MAIN
  ===================================================== */

  return (

    <main className="theme9-checkout-page">


      {/* =================================================
          HEADER
      ================================================= */}

      <section className="theme9-checkout-header">

        <div className="theme9-checkout-container">


          {/* BACK */}

          <button
            type="button"
            className="theme9-checkout-back"
            onClick={handleBackToCart}
          >

            <ArrowLeft
              size={16}
            />

            Back to Cart

          </button>


          {/* TITLE */}

          <div className="theme9-checkout-title-row">


            <div>

              <span className="theme9-checkout-label">

                ALMOST THERE

              </span>


              <h1>
                Checkout
              </h1>


              <p>
                Review your order before
                sending it to the kitchen.
              </p>

            </div>


            {/* TABLE */}

            <div className="theme9-checkout-table">

              <span>
                TABLE
              </span>

              <strong>
                {tableId}
              </strong>

            </div>


          </div>


        </div>

      </section>


      {/* =================================================
          MAIN CONTENT
      ================================================= */}

      <section className="theme9-checkout-content">

        <div className="theme9-checkout-container">

          <div className="theme9-checkout-layout">


            {/* =================================================
                LEFT SIDE
            ================================================= */}

            <div className="theme9-checkout-order">


              {/* =================================================
                  SECTION HEADER
              ================================================= */}

              <div className="theme9-checkout-section-head">

                <div className="theme9-checkout-section-title">

                  <span className="theme9-checkout-step">
                    01
                  </span>


                  <div>

                    <span className="theme9-checkout-small-label">
                      YOUR SELECTION
                    </span>

                    <h2>
                      Review your order
                    </h2>

                  </div>

                </div>


                <span className="theme9-checkout-item-count">

                  {cartCount}

                  {" "}

                  {cartCount === 1
                    ? "item"
                    : "items"}

                </span>

              </div>


              {/* =================================================
                  ORDER ITEMS
              ================================================= */}

              <div className="theme9-checkout-items">

                {cart.map(
                  (item) => {

                    const image =
                      getImageUrl(
                        item.image
                      );


                    return (

                      <article
                        className="theme9-checkout-item"
                        key={item.id}
                      >


                        {/* IMAGE */}

                        <div className="theme9-checkout-item-image">

                          {image ? (

                            <img
                              src={image}
                              alt={item.name}
                            />

                          ) : (

                            <div className="theme9-checkout-placeholder">

                              <Utensils
                                size={23}
                              />

                            </div>

                          )}

                        </div>


                        {/* INFO */}

                        <div className="theme9-checkout-item-info">

                          <span className="theme9-checkout-category">

                            <Leaf
                              size={11}
                            />

                            {item.categoryName ||
                              "FRESH FOOD"}

                          </span>


                          <h3>
                            {item.name}
                          </h3>


                          <p>

                            ₹
                            {formatPrice(
                              item.price
                            )}

                          </p>

                        </div>


                        {/* QUANTITY */}

                        <div className="theme9-checkout-item-quantity">

                          <span>
                            QTY
                          </span>

                          <strong>
                            {item.quantity}
                          </strong>

                        </div>


                        {/* TOTAL */}

                        <div className="theme9-checkout-item-total">

                          <span>
                            ITEM TOTAL
                          </span>

                          <strong>

                            ₹
                            {formatPrice(

                              Number(
                                item.price
                              ) *

                              Number(
                                item.quantity
                              )

                            )}

                          </strong>

                        </div>


                      </article>

                    );

                  }
                )}

              </div>


              {/* =================================================
                  TABLE INFORMATION
              ================================================= */}

              <div className="theme9-table-card">


                <div className="theme9-table-icon">

                  <span>
                    TABLE
                  </span>

                  <strong>
                    {tableId}
                  </strong>

                </div>


                <div className="theme9-table-info">

                  <span>
                    DINING TABLE
                  </span>

                  <h3>
                    Table {tableId}
                  </h3>

                  <p>
                    Your order will be
                    prepared and served
                    for this table.
                  </p>

                </div>


                <div className="theme9-table-confirm">

                  <CheckCircle2
                    size={20}
                  />

                </div>


              </div>


              {/* =================================================
                  INFORMATION
              ================================================= */}

              <div className="theme9-checkout-info">

                <span>

                  <Leaf
                    size={16}
                  />

                </span>


                <p>
                  Please review your
                  items and order total
                  before continuing.
                  Once confirmed, your
                  order can move to the
                  next step.
                </p>

              </div>


            </div>


            {/* =================================================
                RIGHT SUMMARY
            ================================================= */}

            <aside className="theme9-checkout-summary">


              {/* =================================================
                  SUMMARY TOP
              ================================================= */}

              <div className="theme9-summary-top">

                <span className="theme9-summary-number">
                  02
                </span>

                <span className="theme9-summary-label">
                  ORDER TOTAL
                </span>

              </div>


              <h2>
                Your order
              </h2>


              <p className="theme9-summary-subtitle">

                Freshly prepared for
                table {tableId}.

              </p>


              {/* =================================================
                  ITEM COUNT
              ================================================= */}

              <div className="theme9-checkout-count">

                <span>

                  <ShoppingBag
                    size={15}
                  />

                  {cartCount}

                  {" "}

                  {cartCount === 1
                    ? "item"
                    : "items"}

                </span>


                <strong>

                  ₹
                  {formatPrice(
                    cartTotal
                  )}

                </strong>

              </div>


              <div className="theme9-checkout-divider" />


              {/* =================================================
                  SUBTOTAL
              ================================================= */}

              <div className="theme9-checkout-price-row">

                <span>
                  Subtotal
                </span>

                <strong>

                  {loading
                    ? "—"
                    : `₹${formatPrice(
                        priceSummary.subtotal
                      )}`}

                </strong>

              </div>


              {/* =================================================
                  TAX
              ================================================= */}

              <div className="theme9-checkout-price-row">

                <span>
                  Tax
                </span>

                <strong>

                  {loading
                    ? "—"
                    : `₹${formatPrice(
                        priceSummary.tax
                      )}`}

                </strong>

              </div>


              {/* =================================================
                  SERVICE CHARGE
              ================================================= */}

              <div className="theme9-checkout-price-row">

                <span>
                  Service Charge
                </span>

                <strong>

                  {loading
                    ? "—"
                    : `₹${formatPrice(
                        priceSummary.serviceCharge
                      )}`}

                </strong>

              </div>


              <div className="theme9-checkout-divider" />


              {/* =================================================
                  TOTAL
              ================================================= */}

              <div className="theme9-checkout-total">

                <div>

                  <span>
                    TOTAL
                  </span>

                  <small>
                    Including applicable
                    charges
                  </small>

                </div>


                <strong>

                  {loading
                    ? "..."
                    : `₹${formatPrice(
                        priceSummary.total
                      )}`}

                </strong>

              </div>


              {/* =================================================
                  ERROR
              ================================================= */}

              {error && (

                <div className="theme9-checkout-error">

                  <strong>
                    Unable to calculate total
                  </strong>

                  <p>
                    {error}
                  </p>

                </div>

              )}


              {/* =================================================
                  CONTINUE
              ================================================= */}

              <button
                type="button"
                className="theme9-checkout-continue"
                onClick={handleContinue}
                disabled={
                  loading ||
                  Boolean(error)
                }
              >

                {loading ? (

                  <>

                    <span className="theme9-checkout-spinner" />

                    Calculating...

                  </>

                ) : (

                  <>

                    Continue to Order

                    <ArrowRight
                      size={18}
                    />

                  </>

                )}

              </button>


              {/* =================================================
                  BACK TO CART
              ================================================= */}

              <button
                type="button"
                className="theme9-checkout-edit"
                onClick={handleBackToCart}
              >

                <ArrowLeft
                  size={14}
                />

                Edit Your Cart

              </button>


              {/* =================================================
                  SECURE NOTE
              ================================================= */}

              <div className="theme9-checkout-secure">

                <span>

                  <LockKeyhole
                    size={15}
                  />

                </span>


                <p>
                  Payment method will be
                  selected on the next
                  step.
                </p>

              </div>


            </aside>


          </div>

        </div>

      </section>


      {/* =================================================
          FOOTER NOTE
      ================================================= */}

      <section className="theme9-checkout-footer-note">

        <div className="theme9-checkout-container">

          <span>

            <Leaf
              size={16}
            />

          </span>


          <p>
            Fresh ingredients,
            delicious flavours and
            wholesome food prepared
            especially for your table.
          </p>

        </div>

      </section>


    </main>

  );

}


export default CheckoutScreen;